import Page from "../../../../components/common/page/Page";
import { faBookmark, faCalendar, faCartArrowDown, faFileInvoiceDollar, faList, faPlus, faStore } from "@fortawesome/free-solid-svg-icons";
import useAppContext from "../../../../utilities/hooks/useAppContext";
import { Roles } from "../../../../utilities/app-utilities";
import { AppRoutes } from "../../../../config/router";
import { gql } from "@apollo/client";
import { useTranslation } from "react-i18next";
import Datalist, { DatalistPropsType } from "../../../../components/common/datalist/Datalist";
import {
    AgricultureWorkInvestmentDatalistDocument,
    AgricultureWorkInvestmentDatalistQuery,
    AgricultureWorkInvestmentDatalistQueryVariables,
    AgricultureWorkInvestmentFilterInput,
    AgricultureWorkInvestmentSortInput,
    SortEnumType
} from "../../../../types/graphql";
import { appendFilters, DatalistFilterDefinitions, resolveSearchFilterPropertyNames } from "../../../../utilities/datalist/datalist-filter-utilities";
import { graph } from "../../../../config/axios-context";
import HTMLCode from "components/common/HTMLCode";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import Translate from "components/common/Translate";
import { useRef, useState } from "react";
import { DatalistColumnDefinitions, getBottomClassName } from "utilities/datalist/datalist-utilities";
import { uuid } from "config/app-constants";
import { deleteMutation } from "utilities/entities/common/delete-mutation";

type ListType = NonNullable<AgricultureWorkInvestmentDatalistQuery['agricultureWorkInvestments']>['items'][0];

const query = gql`
query AgricultureWorkInvestmentDatalist($where: AgricultureWorkInvestmentFilterInput, $sort: [AgricultureWorkInvestmentSortInput!], $skip: Int, $take: Int) {
  agricultureWorkInvestments {
    items(where: $where, skip: $skip, take: $take, order: $sort) {
      id
      name,
      startDate,
      season {
        name
      }
      unit
      unitPrice
      total
      quantity
      investmentType,
      workers {
        items {
          person {
            name
          }
        }
      }
    }
    count(where: $where),
    totalSum(where: $where, order: $sort)
  }
}
`;

const AgricultureInvestmentDatalistPage = () => {
    const context = useAppContext();
    const [t] = useTranslation();
    const [total, setTotal] = useState<number>(0);
    const datalistRef = useRef<Datalist<ListType, AgricultureWorkInvestmentFilterInput, AgricultureWorkInvestmentSortInput>>(null);

    const pageActions = [
        {
            icon: faList,
            link: AppRoutes.AGRICULTURE_OVERVIEW_MAINTENANCE,
            hide: !context.userHasRole(Roles.AGRICULTURE_WORK_READ),
            title: context.t('pages.agriculture.work.maintenance.list'),
            name: "maintenance-list"
        },
        {
            icon: faPlus,
            hide: !context.userHasRole(Roles.AGRICULTURE_WORK_WRITE),
            link: AppRoutes.AGRICULTURE_INVESTMENT_FORM,
            title: context.t('button.add'),
            name: "add"
        }
    ];

    const datalistConfig: DatalistPropsType<ListType, AgricultureWorkInvestmentFilterInput, AgricultureWorkInvestmentSortInput> = {
        itemId: i => i.id,
        name: 'agricultureInvestmentWorkDatalistPage',
        onItemClick: {
            onClick: i => context.navigate(AppRoutes.AGRICULTURE_INVESTMENT_VIEW, { id: i.id }),
            type: "doubleClick"
        },
        dataSource: {
            sort: [{
                name: 'startDate',
                direction: SortEnumType.Desc,
                id: uuid()
            }],
            request: async (params) => {
                let variables: AgricultureWorkInvestmentDatalistQueryVariables = {
                    skip: params.skip,
                    take: params.take,
                    sort: params.sort,
                    where: params.filters
                };

                if (params.searchText) {
                    const searchFields = [
                        [], // for name
                        ['workers', 'some', 'person']
                    ];
                    const searchNameFIlters = resolveSearchFilterPropertyNames(searchFields, params.searchText) as AgricultureWorkInvestmentFilterInput;
                    searchNameFIlters.or!.push({
                        note: {
                            contains: params.searchText
                        }
                    });

                    variables.where = appendFilters(variables.where, searchNameFIlters);
                }

                const response = await graph<AgricultureWorkInvestmentDatalistQuery, AgricultureWorkInvestmentDatalistQueryVariables>({
                    query: AgricultureWorkInvestmentDatalistDocument,
                    variables
                });

                if (response.errors) {
                    throw response.errors
                }

                setTotal(response.data?.agricultureWorkInvestments?.totalSum ?? 0);
                return response.data?.agricultureWorkInvestments!;
            },
            filter: {
                id: uuid(),
                logic: 'and',
                filters: [DatalistFilterDefinitions.seasonFilterDefinition(context)]
            }
        },
        table: {
            columns: [
                {
                    name: 'id',
                    hide: true
                },
                {
                    name: 'startDate',
                    title: 'fields.date.name',
                    resolve: i => DateUtil.format(new Date(i.startDate), DateFormats.SHORT),
                    filter: {
                        type: 'dateOnly'
                    },
                    sort: true,
                    type: 'dateOnly',
                    className: 'sticky-col',
                    card: {
                        group: 'date-name',
                        title: '',
                        icon: faCalendar
                    }
                },
                {
                    name: 'name',
                    sort: true,
                    filter: {
                        type: "string"
                    },
                    card: {
                        group: 'date-name',
                        title: '',
                        icon: faBookmark
                    }
                },
                {
                    ...DatalistColumnDefinitions.personColumnDefinitionType(context, ['workers', 'items', '0', 'person', 'name']),
                    name: 'store',
                    title: 'fields.store.name',
                    sort: ['workers', 'person', 'name'],
                    filter: {  //FIXME: fix filter with root
                        type: "reference",
                        path: ['workers', 'some', 'person']
                    },
                    card: {
                        title: '',
                        icon: faStore
                    }
                },
                {
                    name: 'quantity',
                    resolve: i => `${i.quantity}  ${t('measurementUnits.' + i.unit)}`,
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        title: '',
                        resolve: i => `${i.quantity} * ${i.unitPrice} ${t('measurementUnits.' + i.unit)}`,
                        icon: faCartArrowDown
                    }
                },
                {
                    name: 'unitPrice',
                    resolve: i => `${i.unitPrice} € / ${t('measurementUnits.' + i.unit)}`,
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        hide: true,
                    }
                },
                {
                    name: 'total',
                    type: 'number',
                    resolve: i => <span>{i.total} <HTMLCode code="euro" /></span>,
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        group: 'price-group',
                        title: '',
                        icon: faFileInvoiceDollar
                    }
                },
                DatalistColumnDefinitions.seasonColumnDefinition(context, 'price-group', true),
            ],
            render: {
                actions: {
                    mutationPermissionRole: Roles.AGRICULTURE_WORK_WRITE,
                    delete: async (item: ListType) => {
                        await deleteMutation('AgricultureWorkInvestmentDatalistDelete', item.id);
                    },
                    editUrl: AppRoutes.AGRICULTURE_INVESTMENT_EDIT,
                    viewUrl: AppRoutes.AGRICULTURE_INVESTMENT_VIEW,
                },
                footer: {
                    table: {
                        bottom: getBottomClassName(true).table,
                        columns: [
                            {
                                name: "total-row",
                                value: <b><Translate i18nKey="fields.total.name" /></b>,
                                className: 'sticky-col'
                            },
                            {
                                name: "total-row-quantity",
                                colSpan: 3,
                            },
                            {
                                name: "total-row-price",
                                value: <span>{total} <HTMLCode code="euro" /></span>
                            }
                        ],
                    }
                }
            }
        }
    };

    return (
        <>
            <Page title="pages.agriculture.work.investment.list" wFull actions={pageActions}>
                <Datalist ref={datalistRef} {...datalistConfig} />
            </Page>
        </>
    )
}

export default AgricultureInvestmentDatalistPage;
